import { faUnlink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useApi } from '@hooks/useApi';
import { Badge, Blockquote, Button, StyledInput } from '@ui';
import Dropdown from '@ui/Dropdown';
import { useConfirmedAction } from 'hooks/useConfirmedAction';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MollieConnectAccount, Profile } from '../types';

interface Props {
    account: MollieConnectAccount;
    canDisconnect: boolean;
    onChange: () => void;
}

export const accountName = (account: MollieConnectAccount) =>
    account.label || account.organisation_name || `Mollie #${account.id}`;

export default function AccountCard({ account, canDisconnect, onChange }: Props) {
    const { t } = useTranslation('general');
    const api = useApi();
    const [label, setLabel] = useState(account.label ?? '');
    const [loading, setLoading] = useState<'profile' | 'default' | 'disconnect' | null>(null);
    const profiles = Object.values(account.profiles || {}) as Profile[];
    const currentProfile = profiles.find((profile) => profile.id === account.current_profile_id);
    const confirmDisconnect = useConfirmedAction({
        message: <p>{t('mollie.assistant.disconnectConfirm')}</p>
    });

    const saveLabel = () => {
        if ((account.label ?? '') === label.trim()) return;
        api({
            url: `connect/${account.id}`,
            method: 'PATCH',
            data: { label: label.trim() || null },
            onSuccess: onChange
        });
    };

    const makeDefault = () => {
        setLoading('default');
        api({
            url: `connect/${account.id}`,
            method: 'PATCH',
            data: { is_default: true },
            onBefore: () => setLoading(null),
            onSuccess: onChange
        });
    };

    const setProfile = (profile: Profile) => {
        if (profile.id === account.current_profile_id) return;
        setLoading('profile');
        api({
            url: `connect/${account.id}/profile/${profile.id}`,
            onBefore: () => setLoading(null),
            onSuccess: onChange
        });
    };

    const disconnect = () => {
        setLoading('disconnect');
        api({
            url: `connect/${account.id}`,
            method: 'DELETE',
            onBefore: () => setLoading(null),
            onSuccess: onChange
        });
    };

    return (
        <div className="tw-card-bordered tw-space-y-3">
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                <div className="tw-min-w-0">
                    <h5 className="tw-mb-0 tw-truncate">{accountName(account)}</h5>
                    {account.label && account.organisation_name && (
                        <span className="tw-text-xs tw-text-accents-6">{account.organisation_name}</span>
                    )}
                </div>
                <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
                    {account.is_default ? (
                        <Badge size="xs" flat>
                            {t('mollie.assistant.defaultBadge')}
                        </Badge>
                    ) : (
                        <Button size="xs" color="light" isLoading={loading === 'default'} onPress={makeDefault}>
                            {t('mollie.assistant.makeDefault')}
                        </Button>
                    )}
                    <Button
                        size="xs"
                        color="error"
                        flat
                        isDisabled={!canDisconnect}
                        isLoading={loading === 'disconnect'}
                        icon={<FontAwesomeIcon icon={faUnlink} />}
                        onPress={() => confirmDisconnect(disconnect)}
                    />
                </div>
            </div>

            {!canDisconnect && (
                <p className="tw-mb-0 tw-text-xs tw-text-accents-6">{t('mollie.assistant.disconnectDefaultHint')}</p>
            )}

            {account.error ? (
                <Blockquote color="warning">{t('mollie.assistant.accountError')}</Blockquote>
            ) : (
                <div>
                    <span className="tw-mb-1 tw-block tw-pl-1 tw-text-sm">{t('mollie.assistant.selectProfile')}</span>
                    <Dropdown
                        block
                        size="sm"
                        color="light"
                        isLoading={loading === 'profile'}
                        label={currentProfile?.name || t('mollie.assistant.selectProfilePlaceholder')}
                    >
                        {profiles.map((profile) => (
                            <Dropdown.Item
                                key={profile.id}
                                label={`${profile.name} (${profile.mode})`}
                                onClick={() => setProfile(profile)}
                            />
                        ))}
                    </Dropdown>
                </div>
            )}

            <StyledInput
                fullWidth
                flush
                label={t('mollie.assistant.labelTitle')}
                placeholder={t('mollie.assistant.labelPlaceholder')}
                value={label}
                maxLength={120}
                onChange={(event) => setLabel(event.target.value)}
                onBlur={saveLabel}
                onKeyDown={(event) => event.key === 'Enter' && saveLabel()}
            />
        </div>
    );
}
